/**
 * App.tsx - Risk Mgmt Panel root.
 *
 * Auth guard wraps the entire app via <RoleGate> (RiskManager OR SuperAdmin).
 * Inside the gate, BrowserRouter at base / + nested routes.
 *
 * 2026-06-11 (Bill): the panel's job RIGHT NOW is the RM Compliance Gating
 * Review — Risk walks the review and their answers are captured server-side.
 * Every surface that is not wired AND runtime-proven is HIDDEN behind
 * SHOW_UNPROVEN so reviewers never see broken screens: Approvals queue /
 * history / approvers (EasyAuth-header backend, panel gets 401), AA queue +
 * Audit Trail (read core /v3/carts/* — function-key gated), adverse-action review +
 * LexisNexis (routes not wired), and the Disclosures workbench (lying-types
 * rendering bugs + edit-modal hydration corruption + placeholder seed data —
 * see project memory). Flip SHOW_UNPROVEN=true in dev to work on them.
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router';

import { RoleGate } from '@/components/RoleGate';
import { ShellLayout } from '@/components/ShellLayout';

import { RiskMgmtHome } from '@/modules/Home/RiskMgmtHome';
import { AboutPage } from '@/modules/About/AboutPage';
import { AdverseActionQueue } from '@/modules/AdverseActionQueue/AdverseActionQueue';
import { AuditTrailExplorer } from '@/modules/AuditTrail/AuditTrailExplorer';
import { LexisNexisScreen } from '@/modules/LexisNexis/LexisNexisScreen';
import { RiskSettingsScreen } from '@/modules/RiskSettings/RiskSettingsScreen';
import { SanctionsScreen } from '@/modules/Sanctions/SanctionsScreen';
import { VerificationsScreen } from '@/modules/Verifications/VerificationsScreen';
import { ApprovalsQueue } from '@/modules/Approvals/ApprovalsQueue';
import { ApprovalsHistory } from '@/modules/Approvals/ApprovalsHistory';
import { ApproversManager } from '@/modules/Approvals/ApproversManager';

import { DisclosuresProvider } from '@/contexts/DisclosuresContext';
import { DisclosuresHome } from '@/modules/Disclosures/DisclosuresHome';
import { TemplateList } from '@/modules/Disclosures/TemplateList';
import { TemplateEditor } from '@/modules/Disclosures/TemplateEditor';
import { VariantEditor } from '@/modules/Disclosures/VariantEditor';
import { SectionsEditor } from '@/modules/Disclosures/SectionsEditor';
import { MergeFieldCatalog } from '@/modules/Disclosures/MergeFieldCatalog';
import { WorkflowLinkMatrix } from '@/modules/Disclosures/WorkflowLinkMatrix';
import { ApprovalQueue } from '@/modules/Disclosures/ApprovalQueue';
import { PackageGenerator } from '@/modules/PackageGenerator/PackageGenerator';

// Lazy — the version page carries the visual body editor (TipTap/ProseMirror chunk).
const VersionEditor = lazy(() => import('@/modules/Disclosures/VersionEditor').then((m) => ({ default: m.VersionEditor })));

// Lazy — the review content (~370 KB extracted JSON) lives in its own chunk.

/**
 * Gate for surfaces that are NOT runtime-proven. Keep false in production
 * until each surface is verified end-to-end against live data.
 */
export const SHOW_UNPROVEN = false;

// Risk Approvals surfaces include `RiskApprover` in addition to the
// panel-wide defaults. Admins + SuperAdmins also pass.
const RISK_APPROVALS_ROLES = [
  'RiskApprover',
  'RiskManager',
  'Admin',
  'SuperAdmin',
] as const;

// Disclosure Engine authoring workbench roles (Tier-3 plan decision #11).
const DISCLOSURE_ROLES = [
  'DisclosureManager',
  'RiskManager',
  'Admin',
  'SuperAdmin',
] as const;

// Package generator — read-only; matches V3RiskPackagePreviewFunction.AllowedRoles.
const PACKAGE_PREVIEW_ROLES = [
  'RiskManager',
  'DisclosureManager',
  'Risk',
  'Admin',
  'SuperAdmin',
] as const;

export function App(): JSX.Element {
  return (
    <RoleGate>
      <BrowserRouter basename="/">
        <Routes>
          <Route element={<ShellLayout />}>
            <Route index element={<RiskMgmtHome />} />
            <Route path="about" element={<AboutPage />} />

            {/* Compliance Gating Review retired 2026-10-06 (Bill: "completed long ago and should be
                purged"); the submitted answers stay in the database as the record. */}

            {/* Risk-Management-owned settings — PROVEN surfaces (2026-09-30): both write
                through /v3/risk/settings, gated on RiskManager. The FraudPoint editor sat
                behind SHOW_UNPROVEN from 2026-09-25 to 2026-09-30 and was unreachable. */}
            <Route path="lexisnexis" element={<LexisNexisScreen />} />
            {/* Risk settings (Bill 2026-10-07): every risk/compliance setting, /v3/risk/settings + /v3/risk/gates. */}
            <Route path="risk-settings" element={<RiskSettingsScreen />} />
            <Route path="sanctions" element={<SanctionsScreen />} />
            <Route path="verifications" element={<VerificationsScreen />} />
            {/* "Disclosures in the signing session" screen removed 2026-10-06 (Bill: "absolutely
                pointless"); its two settings keep their defaults in System settings. */}

            {/* Disclosures (BACKLOG-1744, Bill 2026-10-06: "the disclosure domain is completely
                missing") — templates, versions + approvals, variants, sections, merge fields and the
                workflow association matrix. Server routes accept RiskManager. */}
            <Route
              path="disclosures"
              element={
                <RoleGate allowedRoles={DISCLOSURE_ROLES}>
                  <DisclosuresProvider>
                    <Outlet />
                  </DisclosuresProvider>
                </RoleGate>
              }
            >
              <Route index element={<DisclosuresHome />} />
              <Route path="templates" element={<TemplateList />} />
              <Route path="templates/:code" element={<TemplateEditor />} />
              <Route path="templates/:code/versions/:version" element={<Suspense fallback={<div className="empty">Loading the editor…</div>}><VersionEditor /></Suspense>} />
              <Route path="variants" element={<VariantEditor />} />
              <Route path="sections" element={<SectionsEditor />} />
              <Route path="merge-fields" element={<MergeFieldCatalog />} />
              <Route path="workflow-links" element={<WorkflowLinkMatrix />} />
              <Route path="approval-queue" element={<ApprovalQueue />} />
            </Route>

            {/* Disclosure package generator (Bill 2026-10-07): in-memory test carts through the
                real resolve + render — no carts, CIFs or accounts. Server: /v3/risk/package-preview. */}
            <Route
              path="package-generator"
              element={
                <RoleGate allowedRoles={PACKAGE_PREVIEW_ROLES}>
                  <PackageGenerator />
                </RoleGate>
              }
            />

            {SHOW_UNPROVEN && (
              <>
                <Route path="adverse-action-queue" element={<AdverseActionQueue />} />
                <Route path="audit-trail" element={<AuditTrailExplorer />} />

                <Route
                  path="queue"
                  element={
                    <RoleGate allowedRoles={RISK_APPROVALS_ROLES}>
                      <ApprovalsQueue />
                    </RoleGate>
                  }
                />
                <Route
                  path="history"
                  element={
                    <RoleGate allowedRoles={RISK_APPROVALS_ROLES}>
                      <ApprovalsHistory />
                    </RoleGate>
                  }
                />
                <Route
                  path="approvers"
                  element={
                    <RoleGate allowedRoles={RISK_APPROVALS_ROLES}>
                      <ApproversManager />
                    </RoleGate>
                  }
                />

              </>
            )}

            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </RoleGate>
  );
}
